import { useCallback, useRef } from "react";
import { useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import type { NavigationState } from "@react-navigation/routers";
import { Cct, LppPlan } from "../__generated__/graphql";
import { useSetAtom } from "jotai";
import { editLppPlanCallback } from "../atoms/core";

type ScreenParams = StackScreenProps<RootStackParamList>;

type CallBackType = (lppPlan?: LppPlan | null) => void | Promise<void>;

type CallBackRefType = {
    callback: CallBackType;
    state: NavigationState<RootStackParamList>;
};

const extractLastRouteName = (
    state: NavigationState<RootStackParamList>,
): Extract<keyof RootStackParamList, string> | undefined => {
    const routes = state.routes;
    if (routes.length === 0) {
        return undefined;
    }
    const lastRoute = routes[routes.length - 1];
    return lastRoute.name;
};
/**
 * This will bring the LPP editor on screen and set the plan selection
 * usage:
 * const currentPlan = // assume this exists and is of type LppPlan or null for empty editor
 * const lppEditor = useLPPEditor();
 * lppEditor({currentPlan, availablePlans, cct}, async (plan) => {
 *       // handle plan null=> cancel, not null => have been saved
 *   });
 */
export const useLPPEditor = () => {
    const callbackRef = useRef<CallBackRefType>(undefined);
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const setOnSave = useSetAtom(editLppPlanCallback);
    return useCallback(
        (
            options: {
                currentPlan?: LppPlan | null;
                availablePlans: LppPlan[];
                useLse: boolean;
                cct?: Cct | null;
            },
            onChange: CallBackType,
        ) => {
            const state = navigation.getState();
            callbackRef.current = { callback: onChange, state };

            const onSave = async (plan?: LppPlan) => {
                const callback = callbackRef.current?.callback;
                const savedState = callbackRef.current?.state;
                if (plan !== undefined) {
                    await callback?.(plan);
                } else {
                    await callback?.(undefined);
                }

                callbackRef.current = undefined;

                if (savedState) {
                    const lastRouteName = extractLastRouteName(savedState);
                    if (lastRouteName) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        navigation.navigate(lastRouteName);
                        return;
                    }
                }
                navigation.pop();
            };
            setOnSave({ func: onSave });
            navigation.navigate("LPPEditorNavScreen", {
                lppPlan: options.currentPlan || undefined,
                cct: options.cct ?? undefined,
                useLse: !!options.useLse,
                availablePlans: options.availablePlans,
            });
        },
        [navigation, setOnSave],
    );
};
